/**
 * NotificationKindBadge — maps NotificationKind to icon + color token + label.
 *
 * Rules:
 * - Color is ONE of three signals — every badge has icon + text + color.
 * - All color classes use Tailwind CSS-var-backed semantic tokens. No hex literals.
 * - action_required has a subtle ring pulse for urgency, suppressed under
 *   prefers-reduced-motion via Tailwind's motion-safe: modifier.
 * - role="status" + aria-label for screen-reader accessibility.
 */

import { AlertCircle, CheckCircle, Info, type LucideIcon, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { NotificationKind } from '@/lib/api/notifications';

// ── Config map ────────────────────────────────────────────────────────────────

interface KindConfig {
  icon: LucideIcon;
  classes: string;
  label: string;
}

const KIND_MAP: Record<NotificationKind, KindConfig> = {
  info: {
    icon: Info,
    classes: 'bg-info/15 text-info border border-info/40',
    label: 'Info',
  },
  warning: {
    icon: TriangleAlert,
    classes: 'bg-warning/15 text-warning border border-warning/40',
    label: 'Warning',
  },
  action_required: {
    icon: AlertCircle,
    classes: 'bg-destructive/15 text-destructive border border-destructive/40',
    label: 'Action required',
  },
  success: {
    icon: CheckCircle,
    classes: 'bg-success/15 text-success border border-success/40',
    label: 'Success',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export interface NotificationKindBadgeProps {
  kind: NotificationKind;
  size?: 'sm' | 'default';
  className?: string;
}

export function NotificationKindBadge({
  kind,
  size = 'default',
  className,
}: NotificationKindBadgeProps) {
  const config = KIND_MAP[kind];
  const Icon = config.icon;

  const sizeClasses =
    size === 'sm'
      ? 'gap-1 px-1.5 py-0.5 text-[10px] leading-none [&_svg]:size-3'
      : 'gap-1.5 px-2 py-1 text-xs [&_svg]:size-3.5';

  return (
    <span
      role="status"
      aria-label={config.label}
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        sizeClasses,
        config.classes,
        // Pulsing ring for action_required — suppressed under prefers-reduced-motion
        kind === 'action_required' &&
          'motion-safe:animate-pulse motion-safe:shadow-[0_0_0_2px_hsl(var(--destructive)/0.25)]',
        className
      )}
    >
      <Icon aria-hidden="true" />
      {config.label}
    </span>
  );
}
